import { apiClient } from '@/config/api';
import type {
  PlaybookInstance,
  AvailableTransitionsResponse,
  ExecuteTransitionRequest,
  ExecuteTransitionResponse,
  DryRunResponse,
  ForceTransitionRequest,
  InstanceDiagnostics,
  TransitionLogEntry,
  PlaybookApproval,
  ApproveTransitionRequest,
  PlaybookEntity,
  PlaybookListResponse,
  PlaybookListFilters,
  SimulationResult,
  ValidationResult,
  VersionHistoryEntry,
  PlaybookDiff,
  UpstreamChanges,
  VersionCompatibility,
  MigrationResult,
  BulkMigrationResult,
  PlaybookScheme,
  AnalyticsOverview,
  StatusDwellTime,
  GuardFailureRate,
  ActionErrorRate,
  VersionComparisonItem,
  AnalyticsDateRange,
  WebhookDeliveriesResponse,
  PlaybookTaskType,
  PlaybookStatusFull,
  PlaybookTransitionFull,
  PlaybookTransitionGuard,
  PlaybookTransitionValidator,
  PlaybookTransitionAction,
  TransitionForm,
  CreateStatusRequest,
  UpdateStatusRequest,
  CreateTransitionRequest,
  UpdateTransitionRequest,
  CreateGuardRequest,
  UpdateGuardRequest,
  CreateValidatorRequest,
  UpdateValidatorRequest,
  CreateActionRequest,
  UpdateActionRequest,
  CreateFormRequest,
  UpdateFormRequest,
  SaveDiagramLayoutRequest,
  CourtRuleDeadline,
  AIGeneratePlaybookParams,
  AIGeneratedPlaybook,
  MigrateTemplateParams,
  GapDetectionResult,
  TransitionHelpResult,
} from '../types/playbook.types';

const BASE = '/api/v1/playbook-instances';
const PB = '/api/v1/playbooks';
const ADMIN = '/api/v1/playbooks/admin';
const SCHEMES = '/api/v1/playbook-schemes';

export const playbookApi = {
  // ── Phase III: Instance Operations ──

  listInstances: (matterId: string) =>
    apiClient.get<{ instances: PlaybookInstance[] }>(
      `/api/v1/matters/${matterId}/playbook-instances`
    ),

  createInstance: (matterId: string, data: { playbookId: string; taskTitle?: string }) =>
    apiClient.post<{ instance: PlaybookInstance }>(
      `/api/v1/matters/${matterId}/playbook-instances`,
      data
    ),

  getInstance: (instanceId: string) =>
    apiClient.get<PlaybookInstance>(`${BASE}/${instanceId}`),

  getAvailableTransitions: (instanceId: string) =>
    apiClient.get<AvailableTransitionsResponse>(
      `${BASE}/${instanceId}/transitions`
    ),

  executeTransition: (instanceId: string, transitionId: string, data?: ExecuteTransitionRequest) =>
    apiClient.post<ExecuteTransitionResponse>(
      `${BASE}/${instanceId}/transitions/${transitionId}/execute`,
      data
    ),

  dryRunTransition: (instanceId: string, transitionId: string, data?: { formData?: Record<string, unknown> }) =>
    apiClient.post<DryRunResponse>(
      `${BASE}/${instanceId}/transitions/${transitionId}/dry-run`,
      data
    ),

  approveTransition: (instanceId: string, transitionId: string, data: ApproveTransitionRequest) =>
    apiClient.post<ExecuteTransitionResponse>(
      `${BASE}/${instanceId}/transitions/${transitionId}/approve`,
      data
    ),

  getApprovalStatus: (instanceId: string, transitionId: string) =>
    apiClient.get<PlaybookApproval[]>(
      `${BASE}/${instanceId}/transitions/${transitionId}/approval-status`
    ),

  forceTransition: (instanceId: string, transitionId: string, data: ForceTransitionRequest) =>
    apiClient.post<ExecuteTransitionResponse>(
      `${BASE}/${instanceId}/force-transition/${transitionId}`,
      data
    ),

  resetInstance: (instanceId: string, data: { reason: string }) =>
    apiClient.post<{ success: boolean }>(
      `${BASE}/${instanceId}/reset`,
      data
    ),

  cancelInstance: (instanceId: string, data: { reason: string; archiveTask?: boolean }) =>
    apiClient.post<{ success: boolean }>(
      `${BASE}/${instanceId}/cancel`,
      data
    ),

  getDiagnostics: (instanceId: string) =>
    apiClient.get<InstanceDiagnostics>(
      `${BASE}/${instanceId}/diagnostics`
    ),

  getTransitionLog: (instanceId: string) =>
    apiClient.get<{ logs: TransitionLogEntry[] }>(
      `${BASE}/${instanceId}/log`
    ),

  // ── Phase IV: Playbook Library ──

  listPlaybooks: (filters?: PlaybookListFilters) => {
    const params = new URLSearchParams();
    if (filters?.practice_area) params.set('practice_area', filters.practice_area);
    if (filters?.lifecycle) params.set('lifecycle', filters.lifecycle);
    if (filters?.source) params.set('source', filters.source);
    if (filters?.page) params.set('page', String(filters.page));
    if (filters?.limit) params.set('limit', String(filters.limit));
    const qs = params.toString();
    return apiClient.get<PlaybookListResponse>(`${PB}${qs ? `?${qs}` : ''}`);
  },

  getPlaybook: (playbookId: string) =>
    apiClient.get<PlaybookEntity>(`${PB}/${playbookId}`),

  clonePlaybook: (playbookId: string) =>
    apiClient.post<PlaybookEntity>(`${PB}/${playbookId}/clone`),

  publishPlaybook: (playbookId: string) =>
    apiClient.post<PlaybookEntity>(`${PB}/${playbookId}/publish`),

  archivePlaybook: (playbookId: string) =>
    apiClient.delete<{ success: boolean }>(`${PB}/${playbookId}`),

  simulatePlaybook: (playbookId: string, data?: { allPaths?: boolean; matterContext?: Record<string, unknown> }) =>
    apiClient.post<SimulationResult>(`${PB}/${playbookId}/simulate`, data),

  validatePlaybook: (playbookId: string) =>
    apiClient.post<ValidationResult>(`${PB}/${playbookId}/validate`),

  // ── Phase IV: Admin Library ──

  listSystemPlaybooks: () =>
    apiClient.get<{ playbooks: PlaybookEntity[] }>(`${ADMIN}/system`),

  cloneSystemPlaybook: (playbookId: string) =>
    apiClient.post<PlaybookEntity>(`${ADMIN}/${playbookId}/clone-system`),

  // ── Phase IV: Versions & Diff ──

  getVersionHistory: (slug: string) =>
    apiClient.get<VersionHistoryEntry[]>(`${ADMIN}/slug/${slug}/versions`),

  getPlaybookDiff: (sourceId: string, targetId: string) =>
    apiClient.get<PlaybookDiff>(`${ADMIN}/${sourceId}/diff/${targetId}`),

  getUpstreamChanges: (playbookId: string) =>
    apiClient.get<UpstreamChanges>(`${ADMIN}/${playbookId}/upstream-changes`),

  getVersionCompatibility: (sourceId: string, targetId: string) =>
    apiClient.get<VersionCompatibility>(`${PB}/${sourceId}/compatibility/${targetId}`),

  // ── Phase IV: Migration ──

  migrateInstance: (instanceId: string, data: { targetPlaybookId: string; statusMapping: Record<string, string> }) =>
    apiClient.post<MigrationResult>(`${BASE}/${instanceId}/migrate`, data),

  bulkMigrate: (data: {
    sourcePlaybookId: string;
    targetPlaybookId: string;
    statusMapping: Record<string, string>;
    filter?: { currentStatus?: string; errorState?: string };
  }) => apiClient.post<BulkMigrationResult>(`${BASE}/bulk-migrate`, data),

  // ── Phase IV: Schemes ──

  listSchemes: (practiceArea?: string) => {
    const qs = practiceArea ? `?practice_area=${encodeURIComponent(practiceArea)}` : '';
    return apiClient.get<{ schemes: PlaybookScheme[] }>(`${SCHEMES}${qs}`);
  },

  createScheme: (data: { name: string; description?: string; practice_area?: string; jurisdiction_code?: string; is_default?: boolean }) =>
    apiClient.post<PlaybookScheme>(SCHEMES, data),

  updateScheme: (schemeId: string, data: Partial<{ name: string; description: string; practice_area: string; jurisdiction_code: string; is_default: boolean }>) =>
    apiClient.put<PlaybookScheme>(`${SCHEMES}/${schemeId}`, data),

  addSchemeMapping: (schemeId: string, data: { task_type_id: string; playbook_id: string }) =>
    apiClient.post<unknown>(`${SCHEMES}/${schemeId}/mappings`, data),

  // ── Phase IV: Task Types ──

  listTaskTypes: (params?: { category?: string; practice_area?: string }) => {
    const qs = new URLSearchParams();
    if (params?.category) qs.set('category', params.category);
    if (params?.practice_area) qs.set('practice_area', params.practice_area);
    const s = qs.toString();
    return apiClient.get<{ taskTypes: PlaybookTaskType[] }>(`/api/v1/task-types${s ? `?${s}` : ''}`);
  },

  // ── Phase IV: Analytics ──

  getAnalyticsOverview: (playbookId: string, dateRange?: AnalyticsDateRange) => {
    const qs = buildDateRangeQS(dateRange);
    return apiClient.get<AnalyticsOverview>(`${ADMIN}/${playbookId}/analytics/overview${qs}`);
  },

  getBottlenecks: (playbookId: string, dateRange?: AnalyticsDateRange) => {
    const qs = buildDateRangeQS(dateRange);
    return apiClient.get<StatusDwellTime[]>(`${ADMIN}/${playbookId}/analytics/bottlenecks${qs}`);
  },

  getGuardFailures: (playbookId: string, dateRange?: AnalyticsDateRange) => {
    const qs = buildDateRangeQS(dateRange);
    return apiClient.get<GuardFailureRate[]>(`${ADMIN}/${playbookId}/analytics/guard-failures${qs}`);
  },

  getActionErrors: (playbookId: string, dateRange?: AnalyticsDateRange) => {
    const qs = buildDateRangeQS(dateRange);
    return apiClient.get<ActionErrorRate[]>(`${ADMIN}/${playbookId}/analytics/action-errors${qs}`);
  },

  getVersionComparison: (slug: string) =>
    apiClient.get<VersionComparisonItem[]>(`${ADMIN}/slug/${slug}/analytics/versions`),

  // ── Phase IV: Webhooks ──

  listWebhookDeliveries: (playbookId: string, params?: { status?: string; from?: string; to?: string; limit?: number; offset?: number }) => {
    const qs = new URLSearchParams();
    if (params?.status) qs.set('status', params.status);
    if (params?.from) qs.set('from', params.from);
    if (params?.to) qs.set('to', params.to);
    if (params?.limit) qs.set('limit', String(params.limit));
    if (params?.offset) qs.set('offset', String(params.offset));
    const s = qs.toString();
    return apiClient.get<WebhookDeliveriesResponse>(`${ADMIN}/${playbookId}/webhooks/deliveries${s ? `?${s}` : ''}`);
  },

  retryWebhookDelivery: (playbookId: string, deliveryId: string) =>
    apiClient.post<{ success: boolean; newJobId: string }>(
      `${ADMIN}/${playbookId}/webhooks/deliveries/${deliveryId}/retry`
    ),

  // ── Phase V: Editor — Status CRUD ──

  createStatus: (playbookId: string, data: CreateStatusRequest) =>
    apiClient.post<PlaybookStatusFull>(`${PB}/${playbookId}/statuses`, data),

  updateStatus: (playbookId: string, statusId: string, data: UpdateStatusRequest) =>
    apiClient.put<PlaybookStatusFull>(`${PB}/${playbookId}/statuses/${statusId}`, data),

  deleteStatus: (playbookId: string, statusId: string) =>
    apiClient.delete<{ success: boolean }>(`${PB}/${playbookId}/statuses/${statusId}`),

  // ── Phase V: Editor — Transition CRUD ──

  createTransition: (playbookId: string, data: CreateTransitionRequest) =>
    apiClient.post<PlaybookTransitionFull>(`${PB}/${playbookId}/transitions`, data),

  updateTransition: (playbookId: string, transitionId: string, data: UpdateTransitionRequest) =>
    apiClient.put<PlaybookTransitionFull>(`${PB}/${playbookId}/transitions/${transitionId}`, data),

  deleteTransition: (playbookId: string, transitionId: string) =>
    apiClient.delete<{ success: boolean }>(`${PB}/${playbookId}/transitions/${transitionId}`),

  // ── Phase V: Editor — Guard CRUD ──

  addGuard: (transitionId: string, data: CreateGuardRequest) =>
    apiClient.post<PlaybookTransitionGuard>(`/api/v1/transitions/${transitionId}/guards`, data),

  updateGuard: (transitionId: string, guardId: string, data: UpdateGuardRequest) =>
    apiClient.put<PlaybookTransitionGuard>(`/api/v1/transitions/${transitionId}/guards/${guardId}`, data),

  removeGuard: (transitionId: string, guardId: string) =>
    apiClient.delete<{ success: boolean }>(`/api/v1/transitions/${transitionId}/guards/${guardId}`),

  // ── Phase V: Editor — Validator CRUD ──

  addValidator: (transitionId: string, data: CreateValidatorRequest) =>
    apiClient.post<PlaybookTransitionValidator>(`/api/v1/transitions/${transitionId}/validators`, data),

  updateValidator: (transitionId: string, validatorId: string, data: UpdateValidatorRequest) =>
    apiClient.put<PlaybookTransitionValidator>(`/api/v1/transitions/${transitionId}/validators/${validatorId}`, data),

  removeValidator: (transitionId: string, validatorId: string) =>
    apiClient.delete<{ success: boolean }>(`/api/v1/transitions/${transitionId}/validators/${validatorId}`),

  // ── Phase V: Editor — Action CRUD ──

  addAction: (transitionId: string, data: CreateActionRequest) =>
    apiClient.post<PlaybookTransitionAction>(`/api/v1/transitions/${transitionId}/actions`, data),

  updateAction: (transitionId: string, actionId: string, data: UpdateActionRequest) =>
    apiClient.put<PlaybookTransitionAction>(`/api/v1/transitions/${transitionId}/actions/${actionId}`, data),

  removeAction: (transitionId: string, actionId: string) =>
    apiClient.delete<{ success: boolean }>(`/api/v1/transitions/${transitionId}/actions/${actionId}`),

  // ── Phase V: Editor — Form CRUD ──

  addForm: (transitionId: string, data: CreateFormRequest) =>
    apiClient.post<TransitionForm>(`/api/v1/transitions/${transitionId}/forms`, data),

  updateForm: (transitionId: string, formId: string, data: UpdateFormRequest) =>
    apiClient.put<TransitionForm>(`/api/v1/transitions/${transitionId}/forms/${formId}`, data),

  removeForm: (transitionId: string, formId: string) =>
    apiClient.delete<{ success: boolean }>(`/api/v1/transitions/${transitionId}/forms/${formId}`),

  // ── Phase V: Editor — Diagram Layout ──

  saveDiagramLayout: (playbookId: string, data: SaveDiagramLayoutRequest) =>
    apiClient.put<{ success: boolean }>(`${PB}/${playbookId}/layout`, data),

  // ── Phase V: Court Rules Browser ──

  listCourtRuleDeadlines: (params?: { jurisdiction_code?: string; search?: string }) => {
    const qs = new URLSearchParams();
    if (params?.jurisdiction_code) qs.set('jurisdiction_code', params.jurisdiction_code);
    if (params?.search) qs.set('search', params.search);
    const s = qs.toString();
    return apiClient.get<{ deadlines: CourtRuleDeadline[] }>(`/api/v1/court-rules/deadlines${s ? `?${s}` : ''}`);
  },

  // ── Phase IV: Admin Metadata ──

  listEventSources: () =>
    apiClient.get<{ eventSources: Array<Record<string, unknown>>; unregisteredSourceTypes: string[] }>(`${ADMIN}/event-sources`),

  listActionTypes: () =>
    apiClient.get<Array<{ type: string; label: string; description: string; configSchema?: Record<string, unknown> }>>(`${ADMIN}/action-types`),

  listGuardTypes: () =>
    apiClient.get<Array<{ type: string; label: string; description: string; configSchema?: Record<string, unknown> }>>(`${ADMIN}/guard-types`),

  listValidatorTypes: () =>
    apiClient.get<Array<{ type: string; label: string; description: string; configSchema?: Record<string, unknown> }>>(`${ADMIN}/validator-types`),

  // ── Phase VII: AI Playbook Generation ──

  generatePlaybook: (params: AIGeneratePlaybookParams) =>
    apiClient.post<{ playbook: AIGeneratedPlaybook }>(`${ADMIN}/generate`, params),

  persistGeneratedPlaybook: (generated: AIGeneratedPlaybook) =>
    apiClient.post<{ playbook: Record<string, unknown> }>(`${ADMIN}/generate/persist`, { generated }),

  migrateTemplate: (params: MigrateTemplateParams) =>
    apiClient.post<{ playbook: AIGeneratedPlaybook }>(`${ADMIN}/migrate-template`, params),

  detectGaps: (playbookId: string) =>
    apiClient.post<GapDetectionResult>(`${ADMIN}/${playbookId}/ai/gap-detection`, {}),

  getTransitionHelp: (playbookId: string, instanceId: string, transitionId: string) =>
    apiClient.post<TransitionHelpResult>(`${ADMIN}/${playbookId}/ai/transition-help`, { instanceId, transitionId }),
};

function buildDateRangeQS(dateRange?: AnalyticsDateRange): string {
  if (!dateRange) return '';
  const params = new URLSearchParams();
  if (dateRange.from) params.set('from', dateRange.from);
  if (dateRange.to) params.set('to', dateRange.to);
  const s = params.toString();
  return s ? `?${s}` : '';
}
